import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import {
  TextField,
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
} from "@mui/material";
import { Irai, iraiHeader } from "../types/GxpTypes";
import { useData } from "../utils/DataContext";
import { useAuth } from 'react-oidc-context';
import { validateKi67 } from "../utils/ValidationUtils";

export const translateFieldName = (name: string) => {
  return iraiHeader[name as keyof Irai] as string;
};

const FormControlField = ({
  fieldName,
  rules,
}: {
  fieldName: keyof Irai;
  rules?: any;
}) => {
  const { control } = useFormContext<Irai>();
  const { getMaster } = useData();
  const labelId = fieldName + "-label";
  const labelTxt = translateFieldName(fieldName);
  const master = getMaster(fieldName);

  return (
    <FormControl fullWidth margin="normal">
      <InputLabel id={labelId}>{labelTxt}</InputLabel>
      <Controller
        name={fieldName}
        control={control}
        rules={rules}
        render={({ field, fieldState: { error } }) => (
          <>
            <Select
              labelId={labelId}
              label={translateFieldName(fieldName)}
              {...field}
              value={field.value || ""} // undefinedの場合は空文字列を使用
            >
              {master.map((data) => (
                <MenuItem key={data.id} value={data.id}>
                  {data.name}
                </MenuItem>
              ))}
            </Select>
            {error && <Typography color="error" variant="caption">{error.message}</Typography>}
          </>
        )}
      />
    </FormControl>
  );
};

const FormField = ({
  fieldName,
  fieldValue,
}: {
  fieldName: keyof Irai;
  fieldValue: string | number | Date | null;
}) => {
  const { register, formState: { errors } } = useFormContext<Irai>(); // Enhanced to use control
  const auth = useAuth();

  // console.log(`Rendering field: ${fieldName}, value: ${fieldValue}`);
  // Customized field rendering based on field type
  switch (fieldName) {
    case "patientGender":
    case "diagnosisId":
    case "lymphStsId":
    case "esStsId":
    case "prStsId":
    case "her2StsId":
    case "tclass":
    case "nclass":
    case "mclass":
    case "menstruation":
    case "tumorGrade":
    case "biopsyId":
      return (
        <FormControlField
          fieldName={fieldName}
        />
      );
    case "approvalDoctorName":
      return (
        <TextField
          {...register(fieldName)}
          fullWidth
          margin="normal"
          label={translateFieldName(fieldName)}
          value={auth.user?.profile?.name || ""}
          InputProps={{
            readOnly: true,
          }}
          disabled
        />
      );

    case "patientBirthday":
    case "sampleDate":
      return (
        <TextField
          {...register(fieldName)}
          required
          fullWidth
          margin="normal"
          label={translateFieldName(fieldName)}
          type="date"
          InputLabelProps={{
            shrink: true
          }}
        />
      );
    case "submissionDate":
      return (
        <TextField
          {...register(fieldName)}
          fullWidth
          margin="normal"
          label={translateFieldName(fieldName)}
          type="date"
          InputLabelProps={{
            shrink: true
          }}
        />
      );
    case "specimenId":
      return (
        <TextField
          {...register(fieldName)}
          fullWidth
          margin="normal"
          label={translateFieldName(fieldName)}
          error={!!errors[fieldName]}
          helperText={errors[fieldName]?.message as string}
        />
      );
    case "ki67Per":
      return (
        <TextField
          {...register(fieldName, {
            validate: (value) => {
              const res = validateKi67(value as number | string);
              return res.isValid || res.message;
            }
          })}
          fullWidth
          margin="normal"
          label={translateFieldName(fieldName)}
          error={!!errors[fieldName]}
          helperText={errors[fieldName]?.message as string}
        />
      );
    default:
      return (
        <TextField
          {...register(fieldName)}
          fullWidth
          margin="normal"
          label={translateFieldName(fieldName)}
          error={!!errors[fieldName]}
          helperText={errors[fieldName]?.message as string}
        />
      );
  }
};

type SubFormProps = {
  title: string; // Add a title prop
  subSets: (keyof Irai)[]; // subSets is an array of keys of Irai, which will be input in this sub-form
  currentIrai: Irai;
};

export const SubForm: React.FC<SubFormProps> = ({
  title,
  subSets,
  currentIrai,
}) => (
  <Box sx={{ flexGrow: 1 }}>
    <Typography variant="h6" sx={{ mb: 2 }}>
      {title}
    </Typography>
    {subSets.map((field) => (
      <FormField
        key={field}
        fieldName={field}
        fieldValue={currentIrai[field]}
      />
    ))}
  </Box>
);